import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';
import { memorianTranslations } from '../data/memorianTranslations';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, toggleLang } = useLanguage();
  
  const isMemorianPage = location.pathname === '/become-memorian' || location.pathname === '/become-memorian';
  const t = isMemorianPage ? memorianTranslations[lang] : familyTranslations[lang];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100'
          : 'bg-gray-900/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 select-none">
            <svg width="40" height="52" viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <path 
                d="M5 16C5 7.71573 11.7157 1 20 1C28.2843 1 35 7.71573 35 16V39H5V16Z" 
                fill={scrolled ? "#1f2937" : "white"}
              />
              <rect 
                x="2" 
                y="43" 
                width="36" 
                height="6" 
                fill={scrolled ? "#1f2937" : "white"}
              />
            </svg>
            <div className="flex flex-col">
              <span className={`text-xl font-bold tracking-wide leading-tight ${scrolled ? 'text-gray-800' : 'text-white'}`} style={{ fontFamily: 'Arial, sans-serif', letterSpacing: '0.05em' }}>BEYOND MEMORIA</span>
              <span className={`text-[10px] tracking-wide uppercase ${scrolled ? 'text-gray-600' : 'text-white/80'}`} style={{ fontFamily: 'Arial, sans-serif', letterSpacing: '0.08em', marginTop: '-2px' }}>{t.logoTagline}</span>
            </div>
          </a>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {t.navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => link.isExternal ? navigate(link.href) : handleNavClick(link.href)}
                className={`text-[15px] transition-colors duration-200 font-medium ${
                  scrolled 
                    ? 'text-gray-600 hover:text-gray-900' 
                    : 'text-white hover:text-gray-200'
                }`}
              >
                {link.label}
              </button>
            ))}
            {isMemorianPage ? (
              <button
                onClick={() => navigate('/')}
                className={`text-[15px] transition-colors duration-200 font-medium ${
                  scrolled
                    ? 'text-[#5B4FE9] hover:text-[#4A3ED8]'
                    : 'text-white hover:text-gray-200'
                }`}
              >
                {memorianTranslations[lang].navForFamilies}
              </button>
            ) : (
              <button
                onClick={() => navigate('/become-memorian')}
                className={`text-[15px] transition-colors duration-200 font-medium ${
                  scrolled
                    ? 'text-[#5B4FE9] hover:text-[#4A3ED8]'
                    : 'text-white hover:text-gray-200'
                }`}
              >
                {familyTranslations[lang].navMemorian}
              </button>
            )}
          </div>

          {/* Language Switcher & Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Toggle */}
            <div className="flex items-center gap-1 text-sm font-medium">
              <button
                onClick={() => lang !== 'en' && toggleLang()}
                className={`px-2 py-1 rounded transition-colors duration-200 ${
                  lang === 'en'
                    ? scrolled ? 'text-[#5B4FE9] font-bold' : 'text-white font-bold'
                    : scrolled ? 'text-gray-500 hover:text-gray-700' : 'text-white/70 hover:text-white'
                }`}
              >
                EN
              </button>
              <span className={scrolled ? 'text-gray-300' : 'text-white/50'}>|</span>
              <button
                onClick={() => lang !== 'fr' && toggleLang()}
                className={`px-2 py-1 rounded transition-colors duration-200 ${
                  lang === 'fr'
                    ? scrolled ? 'text-[#5B4FE9] font-bold' : 'text-white font-bold'
                    : scrolled ? 'text-gray-500 hover:text-gray-700' : 'text-white/70 hover:text-white'
                }`}
              >
                FR
              </button>
            </div>
            
            <button
              onClick={() => handleNavClick('#get-started')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#5B4FE9] text-white text-sm font-semibold rounded-full hover:bg-[#4A3ED8] transition-colors duration-200"
            >
              {t.navCta}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-gray-900"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-4 space-y-1">
            {t.navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="block w-full text-left px-3 py-3 text-[15px] text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200 font-medium"
              >
                {link.label}
              </button>
            ))}
            {isMemorianPage ? (
              <button
                onClick={() => { setMobileOpen(false); navigate('/'); }}
                className="block w-full text-left px-3 py-3 text-[15px] text-[#5B4FE9] hover:bg-gray-50 rounded-lg transition-colors duration-200 font-medium"
              >
                {memorianTranslations[lang].navForFamilies}
              </button>
            ) : (
              <button
                onClick={() => { setMobileOpen(false); navigate('/become-memorian'); }}
                className="block w-full text-left px-3 py-3 text-[15px] text-[#5B4FE9] hover:bg-gray-50 rounded-lg transition-colors duration-200 font-medium"
              >
                {familyTranslations[lang].navMemorian}
              </button>
            )}
            
            {/* Mobile Language Toggle */}
            <div className="flex items-center justify-center gap-1 text-sm font-medium py-2">
              <button
                onClick={() => lang !== 'en' && toggleLang()}
                className={`px-3 py-1.5 rounded transition-colors duration-200 ${
                  lang === 'en'
                    ? 'text-[#5B4FE9] font-bold bg-indigo-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                EN
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => lang !== 'fr' && toggleLang()}
                className={`px-3 py-1.5 rounded transition-colors duration-200 ${
                  lang === 'fr'
                    ? 'text-[#5B4FE9] font-bold bg-indigo-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                FR
              </button>
            </div>
            
            <button
              onClick={() => handleNavClick('#get-started')}
              className="w-full mt-3 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#5B4FE9] text-white text-sm font-semibold rounded-full hover:bg-[#4A3ED8] transition-colors duration-200"
            >
              {t.navCta}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
